import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Anchor, Target, Users, TrendingUp } from "lucide-react";

const LIGHTHOUSE_IMG = "https://images.unsplash.com/photo-1696238110083-d374c6248fa0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxsaWdodGhvdXNlJTIwc3Rvcm0lMjBvY2VhbiUyMGRyYW1hdGljJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzczMTY1NjQ3fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } }
};

const values = [
  {
    icon: Target,
    title: "Strategic Vision",
    description: "We see beyond immediate challenges to identify opportunities for lasting transformation and growth."
  },
  {
    icon: Users,
    title: "Active Partnership",
    description: "We don't observe from the sidelines. We work shoulder-to-shoulder with leadership teams."
  },
  {
    icon: Anchor,
    title: "Operational Stability",
    description: "We anchor businesses in proven structures that create accountability and consistent performance."
  },
  {
    icon: TrendingUp,
    title: "Long-term Growth",
    description: "Our focus extends beyond quick fixes to sustainable value creation over time."
  }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl"
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            About Venture Winds
          </motion.p>
          <motion.h1 
            className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8"
            variants={fadeUp}
          >
            Leadership. Structure. Growth.
          </motion.h1>
          <motion.p 
            className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl"
            variants={fadeUp}
          >
            Venture Winds LLC is a strategic business consulting and investment 
            firm that partners with businesses to create long-term value through 
            operational excellence and hands-on leadership.
          </motion.p>
        </motion.div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6"
              variants={fadeUp}
            >
              Our Philosophy
            </motion.h2>
            <motion.p 
              className="text-slate-500 text-lg leading-relaxed mb-6"
              variants={fadeUp}
            >
              Unlike traditional consulting firms that provide recommendations and 
              move on, Venture Winds operates as a true strategic partner. We 
              believe that meaningful business transformation requires sustained 
              involvement, not just advice.
            </motion.p>
            <motion.p 
              className="text-slate-500 text-lg leading-relaxed mb-6"
              variants={fadeUp}
            >
              We selectively identify businesses that align with our long-term 
              goals and work closely with leadership to improve operations, 
              governance, and financial performance. When appropriate, we invest 
              capital and resources to accelerate growth.
            </motion.p>
            <motion.p 
              className="text-slate-500 text-lg leading-relaxed"
              variants={fadeUp}
            >
              Our approach is built on the principle that businesses succeed 
              when they have the right leadership, clear structure, and a 
              strategic vision for the future.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <img
              src={LIGHTHOUSE_IMG}
              alt="Lighthouse guiding through storm"
              className="w-full aspect-[4/3] object-cover"
            />
            <div className="absolute bottom-0 right-0 bg-teal-500 p-8 max-w-xs">
              <p className="text-white text-sm uppercase tracking-widest font-semibold mb-2">
                Guiding Principle
              </p>
              <p className="text-white/90 text-lg font-playfair italic">
                "True partnership means staying the course through calm and storm alike."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="values-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p 
            className="text-teal-600 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Our Values
          </motion.p>
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-16"
            variants={fadeUp}
          >
            What Drives Us
          </motion.h2>

          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            variants={staggerContainer}
          >
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                variants={fadeUp}
                className="bg-white p-8 md:p-10 border border-slate-100 hover:border-teal-500/30 transition-colors"
                data-testid={`value-card-${index}`}
              >
                <value.icon className="w-10 h-10 text-teal-500 mb-6" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl md:text-2xl font-medium text-navy-950 mb-4">
                  {value.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Focus Areas */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Our Focus
          </motion.p>
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-white mb-16"
            variants={fadeUp}
          >
            Where We Create Value
          </motion.h2>

          <motion.div 
            className="grid md:grid-cols-3 gap-px bg-white/10"
            variants={staggerContainer}
          >
            {[
              {
                title: "Operational Restructuring",
                description: "Transforming internal processes and accountability structures for improved performance."
              },
              {
                title: "Strategic Direction",
                description: "Providing executive-level guidance to navigate market challenges and opportunities."
              },
              {
                title: "Growth Investment",
                description: "Deploying capital strategically to accelerate business transformation and scaling."
              }
            ].map((focus, index) => (
              <motion.div
                key={focus.title}
                variants={fadeUp}
                className="bg-navy-950 p-8 md:p-12"
                data-testid={`focus-area-${index}`}
              >
                <span className="text-teal-500 text-sm font-semibold mb-4 block">
                  0{index + 1}
                </span>
                <h3 className="font-playfair text-xl md:text-2xl font-medium text-white mb-4">
                  {focus.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {focus.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="flex flex-col md:flex-row items-center justify-between gap-8"
        >
          <motion.div variants={fadeUp}>
            <h2 className="font-playfair text-2xl md:text-3xl font-semibold text-navy-950 mb-2">
              Ready to transform your business?
            </h2>
            <p className="text-slate-500">
              Explore how Venture Winds can help you achieve operational excellence.
            </p>
          </motion.div>
          <motion.div variants={fadeUp}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-navy-950 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium btn-shadow hover:bg-teal-600 transition-colors"
              data-testid="about-cta"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
